import React, { useMemo, useState } from "react";
import TreeView, { flattenTree } from "react-accessible-treeview";
import cx from "classnames";

// ▶️ Font Awesome Arrow Icon
const FAArrowIcon = ({ isOpen }) => {
  const classes = cx("fa", {
    "fa-caret-down": isOpen,
    "fa-caret-right": !isOpen,
  });
  return <i className={classes} style={{ marginRight: 6 }} />;
};

// 🔨 Build schema validation tree with merged errors
function buildSchemaTreeData(json) {
  const errorMap = new Map();

  json.errors.forEach((error) => {
    const key = error.instancePath || "(no instancePath)";
    const params = error.params || {};

    if (!errorMap.has(key)) {
      errorMap.set(key, { name: `${key}`, children: [] });
    }

    const errorNode = errorMap.get(key);

    // Add allowedValues if present
    if (params.allowedValues) {
      let allowedValuesNode = errorNode.children.find((c) => c.name === "allowedValues");
      if (!allowedValuesNode) {
        allowedValuesNode = { name: "allowedValues", children: [] };
        errorNode.children.push(allowedValuesNode);
      }
      allowedValuesNode.children.push(
        ...params.allowedValues.map((value) => ({ name: value }))
      );
    }

    // Add minimum, maximum, exclusiveMinimum, exclusiveMaximum
    ["minimum", "maximum", "exclusiveMinimum", "exclusiveMaximum"].forEach((paramKey) => {
      if (typeof params[paramKey] !== "undefined") {
        let paramNode = errorNode.children.find((c) => c.name === paramKey);
        if (!paramNode) {
          paramNode = { name: paramKey, children: [] };
          errorNode.children.push(paramNode);
        }
        paramNode.children.push({ name: String(params[paramKey]) });
      }
    });

    // Add pattern
    if (params.pattern) {
      errorNode.children.push({
        name: "pattern",
        children: [{ name: params.pattern }]
      });
    }

    // Add format
    if (params.format) {
      errorNode.children.push({
        name: "format",
        children: [{ name: params.format }]
      });
    }

    // Add const (Ajv sometimes uses allowedValue)
    if (typeof params.allowedValue !== "undefined") {
      errorNode.children.push({
        name: "const",
        children: [{ name: String(params.allowedValue) }]
      });
    }

    // Add the message (merge multiple messages under one node)
    let messageNode = errorNode.children.find((c) => c.name === "message");
    if (!messageNode) {
      messageNode = { name: "message", children: [] };
      errorNode.children.push(messageNode);
    }
    messageNode.children.push({ name: error.message });
  });

  return {
    name: json.key || "Validation Errors",
    children: Array.from(errorMap.values())
  };
}

// ▶️ Schema Validation TreeView Component
export default function CalculationErrorTreeView({ errorResponse }) {
  const treeData = useMemo(() => buildSchemaTreeData(errorResponse), [errorResponse]);
  const flatData = useMemo(() => flattenTree(treeData), [treeData]);

  const [expandedIds, setExpandedIds] = useState();

  const defaultExpandedIds = useMemo(() => {
    const ids = new Set();
    flatData.forEach((node) => {
      if (
        ["allowedValues", "message", "minimum", "maximum", "exclusiveMinimum", "exclusiveMaximum", "pattern", "format", "const"].includes(node.name)
      ) {
        ids.add(node.id);
      }
    });
    return Array.from(ids);
  }, [flatData]);

  return (
    <div>
      <TreeView
        data={flatData}
        aria-label="Schema Validation Tree"
        expandedIds={expandedIds}
        defaultExpandedIds={defaultExpandedIds}
        className="basic"
        nodeRenderer={({
          element,
          isBranch,
          isExpanded,
          isDisabled,
          getNodeProps,
          level,
          handleExpand,
        }) => (
          <div
            {...getNodeProps({ onClick: handleExpand })}
            style={{
              marginLeft: 20 * (level - 1),
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            {isBranch && <FAArrowIcon isOpen={isExpanded} />}
            <span className="name">{element.name}</span>
          </div>
        )}
      />
    </div>
  );
}
